import axiosInstance from './http';

function logAndRethrow(endpoint, error) {
    if (error?.response) {
        console.error(`[contactApi] ${endpoint} -> ${error.response.status}`, {
            url: error.response.config?.url,
            method: error.response.config?.method,
            params: error.response.config?.params,
            data: error.response.data,
            headers: error.response.headers,
        });
    } else {
        console.error(`[contactApi] ${endpoint} -> network/error`, error);
    }
    throw error;
}

export default {
    /**
     * Получение списка контактов для сущности или с фильтрацией
     * @param {object} [params] - Параметры запроса
     * @param {string} [params.target] - Тип сущности ('work' | 'contract' | 'facility' | 'customer')
     * @param {number|string} [params.id] - ID сущности (используется с target)
     * @param {number} [params.page=1] - Номер страницы
     * @param {string} [params.q] - Поисковый запрос
     * @param {number|string} [params.customer] - Фильтр по клиенту (ID)
     * @param {number|string} [params.facility] - Фильтр по объекту (ID)
     * @param {number|string} [params.contract] - Фильтр по договору (ID)
     * @param {number|string} [params.work] - Фильтр по работе (ID)
     * @returns {Promise<{count: Number, next: (String|null), previous: (String|null), results: Array}>}
     */
    async list({ target, id, page = 1, q, customer, facility, contract, work, filters = {} } = {}) {
        const hasTarget = target && (id ?? id === 0);
        const hasFilters = Object.keys(filters).some(key => filters[key] != null);

        try {
            const params = {};
            if (page) params.page = page;
            if (q) params.q = q;
            if (customer) params.customer = customer;
            if (facility) params.facility = facility;
            if (contract) params.contract = contract;
            if (work) params.work = work;

            // Добавляем произвольные фильтры из объекта filters
            Object.keys(filters).forEach(key => {
                if (filters[key] != null) {
                    params[key] = filters[key];
                }
            });

            // Если есть фильтры, используем query параметры вместо пути
            if (hasTarget && !hasFilters) {
                const response = await axiosInstance.get(`contacts/${target}/${id}/`, { params });
                return response.data; // { count, next, previous, results }
            }

            // Если есть target и id, добавляем их в params
            if (hasTarget && hasFilters) {
                params[target] = id;
            }

            const response = await axiosInstance.get('contacts/', { params });
            return response.data; // { count, next, previous, results }
        } catch (error) {
            logAndRethrow(hasTarget && !hasFilters ? `GET contacts/${target}/${id}/` : 'GET contacts/', error);
        }
    },

    /**
     * Создание карточки контакта
     * @param {{name:String, post?:String, email?:String, comments?:String, phones?:String[]}} data
     * @returns {Promise<{id:Number}>}
     */
    async create(data) {
        try {
            const response = await axiosInstance.post('contacts/create/', data);
            return response.data;
        } catch (error) {
            logAndRethrow('POST contacts/create/', error);
        }
    },
    /**
     * Привязка контакта к сущности
     * @param {number|string} contactId
     * @param {{work_id?:Number, contract_id?:Number, customer_id?:Number, facility_id?:Number}} body
     */
    async link(contactId, body) {
        try {
            const response = await axiosInstance.post(`contacts/${contactId}/links/`, body);
            return response.data;
        } catch (error) {
            logAndRethrow(`POST contacts/${contactId}/links/`, error);
        }
    },
    /**
     * Обновить контакт (PATCH)
     * @param {number|string} id
     * @param {Partial<{name:String, post:String, email:String, comments:String, phones:String[]}>} data
     */
    async update(id, data) {
        try {
            const response = await axiosInstance.patch(`contacts/${id}/`, data);
            return response.data;
        } catch (error) {
            logAndRethrow(`PATCH contacts/${id}/`, error);
        }
    },
    /**
     * Удалить контакт
     * @param {number|string} id
     */
    async delete(id) {
        try {
            await axiosInstance.delete(`contacts/${id}/delete/`);
        } catch (error) {
            logAndRethrow(`DELETE contacts/${id}/delete/`, error);
        }
    },



};
